import { type MarketingLocale, useLocalized } from '~/lib/marketing-locale'
import { VisitIcon } from './product-icons'
import { ScrollReveal } from './scroll-reveal'
import { TruchetField } from './truchet-field'

/* Site-visit deep-dive, with gallery, checklist and notes.
 * Copy is locale-keyed — see the note in `crm-section.tsx`. */

type ChecklistItem = { done: boolean; label: string }
type HistoryRow = { date: string; s: number; who: string }

type VisitsContent = {
  heading: string
  tagline: string
  mockupTitle: string
  mockupMeta: string
  statusBadge: string
  photosLabel: string
  notesLabel: string
  annotation: string
  checklistLabel: string
  checklist: readonly ChecklistItem[]
  openItemsLabel: string
  openItems: readonly string[]
  toTaskLabel: string
  cards: {
    field: { title: string; body: string }
    task: { title: string; body: string; items: readonly string[] }
    history: { title: string; body: string; rows: readonly HistoryRow[] }
  }
}

const CONTENT: Record<MarketingLocale, VisitsContent> = {
  en: {
    annotation: 'Maya added a note just now',
    cards: {
      field: {
        body: 'On your phone, one hand free and a hard hat in the other. Photo, comment, and checklist item all in the same place.',
        title: 'Built to use on site',
      },
      history: {
        body: 'The visit history stays with the project. Glance at it and see how the job progressed, visit after visit.',
        rows: [
          { date: 'May 14', s: 1, who: 'Maya' },
          { date: 'May 2', s: 1, who: 'Maya' },
          { date: 'Apr 18', s: 4, who: 'Ryan' },
        ],
        title: 'Project history',
      },
      task: {
        body: 'Every checklist item left open becomes a task on the right project, with an owner and a due date.',
        items: ['Review second-floor electrical', 'Request a tile sample'],
        title: 'Open items become tasks',
      },
    },
    checklist: [
      { done: true, label: 'Check the walls for plumb' },
      { done: true, label: 'Photograph the second-floor slab' },
      { done: false, label: 'Measure the bedroom window opening' },
      { done: false, label: 'Note the outstanding electrical items' },
    ],
    checklistLabel: 'Visit checklist',
    heading: 'Site visits',
    mockupMeta: 'Whitfield House · May 14 · 09:30',
    mockupTitle: 'Site visit',
    notesLabel: 'Notes',
    openItems: ['Review second-floor electrical set', 'Request a tile sample'],
    openItemsLabel: 'Open items created',
    photosLabel: 'Site photos',
    statusBadge: 'In progress',
    tagline: 'The whole site written up before you are back at the office',
    toTaskLabel: '→ Task',
  },
  'pt-BR': {
    annotation: 'Marina anotou agora',
    cards: {
      field: {
        body: 'No celular, com uma mão e o capacete na outra. Foto, comentário e item do checklist tudo no mesmo lugar.',
        title: 'Feito para usar na obra',
      },
      history: {
        body: 'O histórico de visitas fica junto do projeto. Bata o olho e veja como a obra evoluiu, visita após visita.',
        rows: [
          { date: '14 mai', s: 1, who: 'Marina' },
          { date: '02 mai', s: 1, who: 'Marina' },
          { date: '18 abr', s: 4, who: 'Rafael' },
        ],
        title: 'Histórico do projeto',
      },
      task: {
        body: 'Cada item do checklist que ficou pendente vira tarefa no projeto certo, com responsável e prazo.',
        items: ['Revisar elétrica do 2º pav.', 'Solicitar amostra de revestimento'],
        title: 'Pendência vira tarefa',
      },
    },
    checklist: [
      { done: true, label: 'Conferir o prumo das paredes' },
      { done: true, label: 'Fotografar a laje do 2º pavimento' },
      { done: false, label: 'Medir vão de janela do quarto' },
      { done: false, label: 'Anotar pendências da elétrica' },
    ],
    checklistLabel: 'Checklist da visita',
    heading: 'Visitas',
    mockupMeta: 'Residência Lopes · 14 mai · 09:30',
    mockupTitle: 'Visita técnica',
    notesLabel: 'Anotações',
    openItems: ['Revisar projeto elétrico do 2º pav.', 'Solicitar amostra de revestimento'],
    openItemsLabel: 'Pendências geradas',
    photosLabel: 'Fotos da obra',
    statusBadge: 'Em andamento',
    tagline: 'A obra inteira anotada antes de você voltar para o escritório',
    toTaskLabel: '→ Tarefa',
  },
}

const TONE = ['#e8b4a0', '#a9c4e8', '#bcd6a8', '#e8c98a', '#c9b0e0']

const Avatar = ({ seed, size = 22 }: { seed: number; size?: number }) => (
  <span
    className="inline-block shrink-0 rounded-full"
    style={{ background: TONE[seed % TONE.length], height: size, width: size }}
  />
)

const PhotoTile = ({ from, to }: { from: string; to: string }) => (
  <span
    className="block size-full rounded-md"
    style={{ background: `linear-gradient(135deg, ${from}, ${to})` }}
  />
)

const Line = ({ w }: { w: string }) => (
  <span className="block h-[7px] rounded-full bg-[#e9e9eb]" style={{ width: w }} />
)

const VisitMockup = ({ content }: { content: VisitsContent }) => (
  <div className="overflow-hidden rounded-2xl border border-[#dfe5ea] bg-white shadow-[0_24px_60px_-26px_rgba(20,60,80,0.28)]">
    <div className="flex items-center justify-between border-[#eef2f5] border-b px-5 py-3">
      <div>
        <p className="font-semibold text-[#1c1c20] text-[13px]">{content.mockupTitle}</p>
        <p className="text-[#9a9aa0] text-[11px]">{content.mockupMeta}</p>
      </div>
      <span className="rounded-md bg-[#1d7a8c] px-2 py-1 text-[10px] text-white">
        {content.statusBadge}
      </span>
    </div>

    <div className="grid grid-cols-[1.1fr_1fr]">
      {/* Left: gallery + notes */}
      <div className="border-[#eef2f5] border-r p-4">
        <p className="font-medium text-[#43434a] text-[11.5px]">{content.photosLabel}</p>
        <div className="mt-2 grid grid-cols-3 gap-1.5">
          <div className="col-span-2 h-[88px]">
            <PhotoTile from="#dde7f4" to="#a3bdd6" />
          </div>
          <div className="h-[88px]">
            <PhotoTile from="#e7e0d2" to="#bca687" />
          </div>
          <div className="h-[58px]">
            <PhotoTile from="#d8ddd3" to="#9ab09a" />
          </div>
          <div className="h-[58px]">
            <PhotoTile from="#ecd4cb" to="#c89b8a" />
          </div>
          <div className="h-[58px]">
            <PhotoTile from="#dfe1eb" to="#9ea5be" />
          </div>
        </div>

        <p className="mt-4 font-medium text-[#43434a] text-[11.5px]">{content.notesLabel}</p>
        <div className="mt-2 space-y-1.5">
          <Line w="100%" />
          <Line w="92%" />
          <Line w="76%" />
          <Line w="54%" />
        </div>
        <div className="mt-3 flex items-center gap-1.5">
          <Avatar seed={1} size={16} />
          <span className="text-[#9a9aa0] text-[10.5px]">{content.annotation}</span>
        </div>
      </div>

      {/* Right: checklist + open items */}
      <div className="p-4">
        <p className="font-medium text-[#43434a] text-[11.5px]">{content.checklistLabel}</p>
        <div className="mt-2 space-y-2">
          {content.checklist.map((item) => (
            <div className="flex items-center gap-2" key={item.label}>
              <span
                className="flex size-[14px] items-center justify-center rounded-[4px] text-[9px] text-white"
                style={{
                  background: item.done ? '#1d7a8c' : 'transparent',
                  border: item.done ? 'none' : '1.5px solid #d4d4d6',
                }}
              >
                {item.done ? '✓' : ''}
              </span>
              <span
                className="text-[11.5px]"
                style={{
                  color: item.done ? '#9a9aa0' : '#43434a',
                  textDecoration: item.done ? 'line-through' : 'none',
                }}
              >
                {item.label}
              </span>
            </div>
          ))}
        </div>

        <p className="mt-4 font-medium text-[#43434a] text-[11.5px]">{content.openItemsLabel}</p>
        <div className="mt-2 space-y-1.5 rounded-lg border border-[#eef2f5] bg-[#fafbfc] p-2.5">
          {content.openItems.map((item, i) => (
            <div className="flex items-center gap-2" key={item}>
              <span
                className="size-[6px] rounded-full"
                style={{ background: i === 0 ? '#e0593f' : '#e8a13d' }}
              />
              <span className="text-[#43434a] text-[11.5px]">{item}</span>
              <span className="ml-auto text-[#9a9aa0] text-[10px]">{content.toTaskLabel}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
)

const Card = ({
  title,
  body,
  children,
}: {
  title: string
  body: string
  children: React.ReactNode
}) => (
  <div className="rounded-2xl border border-[#dfe5ea] bg-white/80 p-6">
    <h3 className="font-bold text-[#1c1c20] text-[17px]">{title}</h3>
    <p className="mt-2 text-[#6e7780] text-[14.5px] leading-relaxed">{body}</p>
    <div className="mt-5">{children}</div>
  </div>
)

export const VisitsSection = () => {
  const content = useLocalized(CONTENT)

  return (
    <section
      className="relative overflow-hidden py-28"
      style={{
        background: 'linear-gradient(180deg, #ffffff 0%, #ecf2f4 40%, #dfe9ec 100%)',
      }}
    >
      <TruchetField
        className="absolute inset-0 h-full w-full text-[#0b0b0d] opacity-[0.05]"
        seed="visits"
      />
      <div className="relative mx-auto max-w-[1120px] px-6">
        <ScrollReveal className="text-center">
          <div className="flex items-center justify-center gap-3">
            <VisitIcon className="size-[56px]" />
            <h2 className="font-display text-[#0b0b0d] text-[clamp(48px,8vw,76px)] tracking-[-0.01em]">
              {content.heading}
            </h2>
          </div>
          <p className="mt-3 text-[#6e7780] text-[clamp(18px,2.6vw,24px)]">{content.tagline}</p>
        </ScrollReveal>

        <ScrollReveal className="mt-14" delay={100}>
          <VisitMockup content={content} />
        </ScrollReveal>

        <div className="mt-10 grid gap-6 md:grid-cols-3">
          <ScrollReveal>
            <Card body={content.cards.field.body} title={content.cards.field.title}>
              <div className="grid grid-cols-3 gap-1.5">
                <div className="h-12">
                  <PhotoTile from="#dde7f4" to="#a3bdd6" />
                </div>
                <div className="h-12">
                  <PhotoTile from="#e7e0d2" to="#bca687" />
                </div>
                <div className="h-12">
                  <PhotoTile from="#d8ddd3" to="#9ab09a" />
                </div>
              </div>
            </Card>
          </ScrollReveal>
          <ScrollReveal delay={100}>
            <Card body={content.cards.task.body} title={content.cards.task.title}>
              <div className="space-y-2 rounded-lg border border-[#eef2f5] bg-white p-3">
                {content.cards.task.items.map((item, i) => (
                  <div className="flex items-center gap-2 text-[12px]" key={item}>
                    <span
                      className="size-[6px] rounded-full"
                      style={{ background: i === 0 ? '#e0593f' : '#e8a13d' }}
                    />
                    <span className="truncate text-[#43434a]">{item}</span>
                  </div>
                ))}
              </div>
            </Card>
          </ScrollReveal>
          <ScrollReveal delay={200}>
            <Card body={content.cards.history.body} title={content.cards.history.title}>
              <div className="space-y-2">
                {content.cards.history.rows.map((row) => (
                  <div className="flex items-center gap-2" key={row.date}>
                    <Avatar seed={row.s} size={18} />
                    <span className="text-[#43434a] text-[12px]">
                      {row.date} · {row.who}
                    </span>
                  </div>
                ))}
              </div>
            </Card>
          </ScrollReveal>
        </div>
      </div>
    </section>
  )
}
